import { useEffect, useMemo, useState } from 'react'
import { colors, fonts } from "../../../Styles"
import { useTranslation } from 'react-i18next';
import { AttentionTypes } from '../../../Definitions/valueTypes';

// import { TestData as PenData} from '../../../Assets/TestData/lf_Data';
import { _attentionColor } from '../../../Definitions';
import { LfLocation } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall';
import { useLanControllers } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useLanControllers';
import { useTerminals } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useTerminals';
import { useSection } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections';
import { useAppSelector } from '../../../Store';
import { Graph_QL, useGetDomainLog } from '../../../Services';
import { LanControllerLogArgs } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLanControllerLogs';

interface PenAttention_Props {
  pen: LfLocation;
  hasHoverEffect?: boolean
  onClick?: () => void
  gridArea?: string
}
/**
 * PEN_ATTENTION
 * @param penId Unique identifier for each pen.
 * @param hasHoverEffect Boolean that, if true, adds a hover effect to the pen.
 * @param onClick Dynamic onClick eventhandler.
 * @returns a pen where bg color is based on attentiontype.
 */
export default function PEN_ATTENTION({ pen, hasHoverEffect, onClick, gridArea }: PenAttention_Props) {
  const {t} = useTranslation()
  const [animate, setAnimate] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm })), [farm]);
    
  const { data: LogData } = useGetDomainLog() //todo: current LogData is redux and containing both Domain log and LanController log
  const { data: terminals } = useTerminals(connection)

  useEffect(() => {
    setAnimate(true);
  }, []);

  const animal = useMemo(() => {
    return (
      pen.animals && pen.animals.length ? pen.animals[0] : undefined
    )
  }, [pen.animals])
  
  const TerminalDatas = useMemo(() => {
    if (!LogData || !Object.entries(LogData).length) return [];
  
    const controllerData = Object.entries(LogData)[0][1].data as unknown as LanControllerLogArgs[];
    const filteredLogs = controllerData.filter(log => 
      (log.level === 0 || log.level === 1) && (log.type === 1 || log.type === 2)
    );
  
    return filteredLogs.map(log => {
      const TerminalData = terminals[domain]?.data.find(terminal => terminal.terminal_id === log.fk_terminals);
      if (TerminalData) {
        return {
          TerminalData,
          level: log.level,
          attentionColor: log.level === 0 ? colors.systemRed : colors.systemYellow,
        };
      }
      return null;
    }).filter(data => data !== null);
  }, [LogData, domain, terminals]);

  const hasAttention = useMemo(() => {
    return TerminalDatas.some(data => data?.TerminalData?.fk_locations === pen.location_id);
  }, [TerminalDatas, pen.location_id]);

  const handleMouseEnter = () => {
    if (hasHoverEffect && animal) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (hasHoverEffect && animal) {
      setIsHovered(false);
    }
  };

  return (
    <div 
      onClick={animal? onClick : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ 
        width: "80px",
        height: "30px",
        cursor: hasHoverEffect && animal ? "pointer" : "",
        gridArea: gridArea ?? undefined
      }}
    >
      <div style={{
        position: "relative",
        width: "100%",
        height: "100%",
        textAlign: 'center',
        alignContent: "center",
        backgroundColor: isHovered ? colors.greyLightPrimary : colors.greyLightSecondary,
        transition: 'filter 0.3s',
        color: colors.greyDarkPrimary,
        ...fonts.paragraph,
        ...fonts.fontSize4,
      }}>
        {animal ?
          <span>{animal.animal_number}</span>
          : <span style={{opacity: "0.5"}}>{t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty pen"})}</span>
        }
        {hasAttention && TerminalDatas.map((data, index) => (
          data?.TerminalData?.fk_locations === pen.location_id && (
            <div key={index} style={{ top: "0", left: "0", position: "absolute", width: animate ? "10px" : "0px", height: "30px", backgroundColor: data.attentionColor, transition: "width 0.5s ease" }}></div>
          )
        ))}
      </div>
    </div>
  );
}
